import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, Pipe, PipeTransform } from '@angular/core';
import { CallProfileValidationResult, DialerTestSpecContext, isFinishedTest, NetworkStatus, OfonoPhoneService, RootTestResult } from 'dialer';
import { DialerConfig, DialerConfigService, DialerTestService, NetworkStatusEvent, RootTest, TestResultCallStats } from 'dialer';
import { BsModalService } from 'ngx-bootstrap/modal';
import { combineLatest, firstValueFrom, merge, Observable, race, Subject, Subscription, throwError, timer } from 'rxjs';
import { distinctUntilKeyChanged, filter, first, map, skip, switchMap, tap } from 'rxjs/operators';
import { AppConfig } from '../../../../environments/environment';
import { AppState } from '../../../core';
import { activity_status, DriveTestPoint, DriveTestPointSeq, DriveTestSeqStatus, PathTestPoint, PathTestPointSeq, TestSeqStatus } from '../../../core/models';
import { DriveTestSeqDoc } from '../../../core/schema/drive-test-seq-schema';
import { PathTestDoc } from '../../../core/schema/path-test-schema';
import { TestSeqDoc } from '../../../core/schema/test-seq-schema';
import { DialerClientService, DriveTestSeqService, DriveTestService, PathTestService, PointTestLogService } from '../../../core/services';
import { TestSeqService } from '../../../core/services/test-seq.service';
import { TestbedRxdbGraphQlSyncError } from '../../../core/services/testbed-rxdb/testbed-rxdb-error';
import { DialerClientTestSeqModalComponent } from '../../../dialer-client/dialer-client-testseq/dialer-client-testseq-modal/dialer-client-testseq-modal.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';


@Pipe({name: 'seqToStatusId'})
export class SeqToStatusIdPipe implements PipeTransform {
  transform(point: PathTestPoint | DriveTestPoint): number {
    let statusId = point.statusId
    return statusId
  }
}

class TestSeqData {
  seqid: string = ''
  endTime: string = null
  dialer: RootTest = null
  status: string = null
  stats: TestResultCallStats
}

@Component({
  selector: 'app-path-test-point-seq-status-editor',
  templateUrl: './path-test-point-seq-status-editor.component.html',
  styleUrls: ['./path-test-point-seq-status-editor.component.scss']
})
export class PathTestPointSeqStatusEditorComponent implements OnInit, OnDestroy {

  isSaving = false;
  timer;
  duration;
  processing = false
  processingTitle = ""

  useDialer = false
  useBaro = false;

  rootTest: RootTest
  dialerTestSubscription: Subscription

  testSeqData$ : Observable<TestSeqData>

  //pauseMode : 'NOT_PAUSABLE' | 'PAUSABLE' | 'RESUMABLE' = 'NOT_PAUSABLE'

  // cache some status codes so angular change detection can efficiently keep button states
  canStartByStatusId = {
    [activity_status.Id.NOT_STARTED]: true,
    [activity_status.Id.COMPLETE]: true
  }
  canStopByStatusId = {
    [activity_status.Id.IN_PROGRESS]: true
  }

  recoverDialerResultSub: Subscription
  networkStatusSub: Subscription

  @Input() pathTestDoc: PathTestDoc

  @Input('testSeqDoc') set testSeqDocSet( testSeqDoc: TestSeqDoc | DriveTestSeqDoc ) {
    console.log(`set testSeqDoc(${testSeqDoc?.seqid})`)
    //this.pauseMode = 'NOT_PAUSABLE'
    this.recoverDialerResultSub?.unsubscribe()
    this.testSeqDoc = testSeqDoc
    this.testSeqData$ = testSeqDoc?.$.pipe( map( doc => {
      return {
        seqid: doc.seqid,
        endTime: doc.endTime,
        dialer: doc.dialer,
        status: doc.status,
        stats: this.dialerTestService.computeCallStats(doc.dialer)
      }
    }))
    if ( this.seq && testSeqDoc && this.seq.dialerSeqId && this.seq.dialerSeqId == testSeqDoc.seqid ) {
      //this.updatePauseMode(this.rootTest)
      //
      // Try to recover test in progress if web page reloaded
      //
      if ( this.isTestInProgress() ) {
        this.recoverDialerResultSub = combineLatest([
          this.dialerClientService.dialerService.initialized$.pipe(filter(bInitialized=>bInitialized)),
          this.dialerClientService.networkStatus$.pipe(
            filter(status=>[NetworkStatusEvent.WsConnected,NetworkStatusEvent.WsReconnected].includes(status.eventType) && status.ok)
          )
        ]).pipe(
          switchMap( async (_) => {
            try {
              return await firstValueFrom(this.dialerTestService.createFetchRootTest$());
            } catch ( e: any ) {
              console.warn('Failed graphql fetch of inprogress root test');
              console.error(e);
              return null;
            }
          }),
          filter( rootTestResult => rootTestResult !== null )
        ).subscribe( (rootTestResult: RootTestResult)  => {
          if ( testSeqDoc !== this.testSeqDoc) {
            console.warn('Recovered root test data for a different test seq doc (ignoring)');
            return;
          }
          if ( !rootTestResult.success ) {
            console.warn('GraphQL error during recovery of root test');
            console.error(rootTestResult.errors);
            return;
          }
          const resultTest = rootTestResult.test;
          if ( resultTest?.seqId !== testSeqDoc.seqid ) {
            if ( testSeqDoc.dialer ) {
              this.componentErrorMsg$.next(`The test sequence is no longer active on the dialer box. Restoring from last known value.`)
              this.rootTest = testSeqDoc.dialer
              delete this.rootTest.pausable;
              this.rootTest.status = 'PAUSED'
              this.bRootTestActive = true;
            } else {
              console.dir(testSeqDoc.toJSON())
              this.componentErrorMsg$.next(`The test sequence is no longer active on the dialer box. Data may be lost (ensure dialer is connected).`)
            }
          } else {
            console.warn('Connection recovered with rootTest:',rootTestResult)
            this.bRootTestActive = !isFinishedTest(resultTest);
            this.rootTest = resultTest;
            this.beginTestSubscription()
          }
        })
      }
    }
  }
  testSeqDoc: TestSeqDoc | DriveTestSeqDoc

  @Input('seq') set inputSeq(seq: PathTestPointSeq) {
    console.log(`set seq(${seq?.id},dialerSeqId=${seq?.dialerSeqId})`)
    this.recoverDialerResultSub?.unsubscribe()
    this.seq = seq
    const { startTime, stopTime } = seq || {}
    this.resetRootTestErrorEmitter()
    clearInterval(this.timer)
    if(startTime && !stopTime) {
      var start = new Date(startTime)
      this.timer = setInterval(() => {
        var end = new Date()
        this.duration = end.getTime() - start.getTime()
      }, 1000);
    } else {
      this.duration = new Date(stopTime).getTime() - new Date(startTime).getTime()
    }
    this.bRootTestActive = false;
    if ( this.rootTest?.seqId !== seq.dialerSeqId ) {
      this.rootTest = null;
    }
  }

  @Input('testPoint') set inputTestPoint(pt: PathTestPoint | DriveTestPoint) {
    this.testPoint = pt
  }

  @Input() saveTestPointSeqImplFn : ( testPointId: string, seq:PathTestPointSeq | DriveTestPointSeq, statusId: activity_status.Id) =>
      Promise<[PathTestPoint,PathTestPointSeq] | [DriveTestPoint,DriveTestPointSeq]  >

  @Input() buildDialerConfigFn : () => DialerConfig

  @Output() testRestarted = new EventEmitter<PathTestPoint | DriveTestPoint>()

  public testPoint: PathTestPoint | DriveTestPoint
  public seq: PathTestPointSeq | DriveTestPointSeq

  useDialer$ = new EventEmitter<boolean>()
  errorMsg$ : Observable<string>
  componentErrorMsg$ = new Subject<string>()
  currentUserId: string

  bRootTestActive = false;
  rootTestPausing$ = this.dialerTestService.pausingTestId$.pipe( map( pausingId => pausingId && pausingId==this.rootTest?.id));
  rootTestResuming$ = this.dialerTestService.resumingTestId$.pipe( map( resumingId => resumingId && resumingId==this.rootTest?.id));
  rootTestStopping$ = this.dialerTestService.stoppingTestId$.pipe( map( stoppingId => stoppingId && stoppingId==this.rootTest?.id));

  pathTestService: PathTestService | DriveTestService

  profileFieldTitles = {
    callCount: "Call Count",
    callDurationSecs: "Call Duration (secs)",
    callWaitSecs: "Call Wait (secs)",
    callSetupTimeoutSecs: "Call Setup Timeout (secs)",
  }
  profileValidationErrors: CallProfileValidationResult = {};
  haveEnforcedProfileErrors = false;
  profileValidationRulesChangedSubscription: Subscription;
  profileChangedSubscription: Subscription;

  constructor(
    private appState: AppState,
    private _pathTestService: PathTestService,
    private _driveTestService: DriveTestService,
    private modalService: BsModalService,
    public dialerConfigService: DialerConfigService,
    public dialerClientService: DialerClientService,
    public dialerTestService: DialerTestService,
    public pointTestLogService: PointTestLogService,
    public testSeqService: TestSeqService,
    public driveTestSeqService: DriveTestSeqService,
    private phoneService: OfonoPhoneService
    ) {
      this.useDialer = this.appState.settings.getPathTestPointEditorUseDialer();
      this.useBaro = this.appState.settings.getPathTestPointEditorUseBaro();
    }

    dialerInitializedSubscription: Subscription

  ngOnInit(): void {
    this.currentUserId = this.appState.getUser()?._id
    if(this.pathTestDoc.type == "drive test") {
      this.pathTestService = this._driveTestService
    } else if(this.pathTestDoc.type == "path test") {
      this.pathTestService = this._pathTestService
    }
    this.profileValidationRulesChangedSubscription = this.dialerClientService.profileValidationRulesChanged$.subscribe( validationRules => {
      this.validateProfile();
    });
    this.profileChangedSubscription = this.dialerClientService.dialerConfigService.profileSaved$.subscribe( profile => {
      this.validateProfile();
    });
  }

  beginProcessing(title:string) {
    this.componentErrorMsg$.next(null);
    this.processingTitle = title
    this.processing = true
  }

  endProcessing() {
    this.processing = false
    this.processingTitle = ""
  }

  isTestInProgress = () => this.seq && this.seq.startTime && !this.seq.stopTime;

  isLocalTestInProgress = () => {
    let bInProgress = this.isTestInProgress();
    if ( bInProgress ) {
      const seqId = this.testSeqDoc && this.testSeqDoc.seqid;
      console.log(`isLocalTestInProgress() - seqId=${seqId}, currentDialerTest.seqId=${this.dialerClientService.currentDialerTest?.seqId}`);
      bInProgress = seqId != null && seqId == this.dialerClientService.currentDialerTest?.seqId;
    }
    console.log(`isLocalTestInProgress()? ${bInProgress}`);
    return bInProgress;
  }

  lastSyncError : TestbedRxdbGraphQlSyncError

  resetRootTestErrorEmitter() {
    this.errorMsg$ = merge(
      this.componentErrorMsg$,
      this.dialerClientService.rootTestProcessingError$.pipe(
        filter( e => e.rootTest && e.rootTest.id == this.rootTest.id ),
        map( e => e.errorMsg )
      ),
      this.testSeqService.error$.pipe(
        //tap( e => console.dir(e)),
        map( e => this.testSeqService.wrapSyncError(e) ),
        filter( e => !['createTestSeqs','updateTestSeqs'].includes(e.error?.path) ),
        filter( syncError => !syncError.equals(this.lastSyncError) ),
        tap( syncError => this.lastSyncError = syncError),
        map( syncError => (syncError.extractDocId() == this.seq?.dialerSeqId) ? //this.testseqDoc?.seqid
          'Error occured uploading/syncing: ' + syncError.extractReason() :
          'Database sync errors with test seq collection (check logs)'
        )
      ),
      this.driveTestSeqService.error$.pipe(
        //tap( e => console.dir(e)),
        map( e => this.driveTestSeqService.wrapSyncError(e) ),
        filter( e => !['createDriveTestSeqs','updateDriveTestSeqs'].includes(e.error?.path) ),
        filter( syncError => !syncError.equals(this.lastSyncError) ),
        tap( syncError => this.lastSyncError = syncError),
        map( syncError => (syncError.extractDocId() == this.seq?.dialerSeqId) ? //this.testseqDoc?.seqid
          'Error occured uploading/syncing: ' + syncError.extractReason() :
          'Database sync errors with drive test seq collection (check logs)'
        )
      )
    )
  }

  ngOnDestroy() {
    this.dialerTestSubscription?.unsubscribe()
    this.recoverDialerResultSub?.unsubscribe()
    this.networkStatusSub?.unsubscribe()
    this.profileChangedSubscription?.unsubscribe()
    this.profileValidationRulesChangedSubscription?.unsubscribe()
  }

  async validateProfile() {
    this.dialerClientService.dialerConfigService.loadProfile();
    // console.log('validateProfile()', {
    //  profile: this.dialerConfigService.defaultProfile,
    //  validationRules: this.dialerConfigService.profileValidationRules
    //});
    this.profileValidationErrors = this.dialerConfigService.validateProfile(
      this.dialerConfigService.defaultProfile, {
        buildingId: this.pathTestDoc.buildingId,
        stage: this.pathTestDoc.stage
      });
    this.haveEnforcedProfileErrors = Object.values(this.profileValidationErrors).some( errors => errors.some( error => error.enforce ));
  }

  async start() {
    this.isSaving = true
    this.beginProcessing('Starting')
    const testPoint = this.testPoint
    const seq = this.seq
    try {
      if(seq?.startTime) {
        const yesFunction = async () => {
          await this.startTest(testPoint,seq)
          this.testRestarted.emit(testPoint)
        }

        let initialState = {
          onYes: yesFunction,
          question: 'Do you wish to restart the test?\n This will reset the call counts.',
          focusOnNo: true,
          yesStyle: 'btn btn-danger'
        }
        this.modalService.show(ConfirmModalComponent, { initialState });
      } else {
        this.startTest(testPoint,seq)
      }
    } catch (err) {
      this.pathTestService.publishError(err,this)
      const errMsg = this.pathTestService.extractErrorMsg(err)
      this.componentErrorMsg$.next(errMsg)
      //this.appState.notifyError('Error starting. ' + this.pathTestService.extractErrorMsg(err),this,3)
    }
    this.isSaving = false
    this.endProcessing()
  }

  stop = async (bStopDialerTest = true) => {
    this.isSaving = true
    this.beginProcessing('Stopping')
    let rootTest = this.rootTest
    const seq = this.seq
    const testPoint = this.testPoint
    const testSeqDoc = this.testSeqDoc;

    try {
      if (bStopDialerTest && rootTest) {
        const testId = rootTest['id']
        try {
          console.log('Stopping dialer test')
          const resultTestStatus = await this.dialerClientService.stopTest(testId);
          await this.pointTestLogService.logComment(seq.id, `[Stopped] Status is ${resultTestStatus}`);
        } catch (error) {
          let bForcedStop = false
          let initialState = {
            onYes: async () => {
              await this.stop(false);
              bForcedStop = true
              if (seq?.id) {
                await this.pointTestLogService.logComment(seq.id, '[Error] Could not get data from dialer box.')
                await this.pointTestLogService.logComment(seq.id, '[Stop]')
              }
            },
            question: 'Could not stop test on dialer box.  Force stop?',
            focusOnNo: true,
            yesStyle: 'btn btn-danger'
          }
          const modalRef = this.modalService.show(ConfirmModalComponent, { initialState });
          await firstValueFrom(modalRef.onHidden.pipe(first()))
          if (!bForcedStop) {
            throw error
          }
        }
      }
      const updatedTestPt = await this.pathTestService.stopTest(this.pathTestDoc, testPoint, seq)
      clearInterval(this.timer)
      const pathTest = this.pathTestDoc
      const bIsDriveTest = pathTest.type == 'drive test';
      if (!rootTest) {
        const statusMsg = this.pointTestLogService.textForStatusId(updatedTestPt.statusId)
        await this.pointTestLogService.logComment(seq.id, statusMsg)
        if (pathTest && testPoint && seq) {
          try {
            if (bIsDriveTest) {
              var noDialerDriveTestSeqDoc = this.driveTestSeqService.rxCollection.newDocument({
                seqid: seq.id,
                testPointId: testPoint.id,
                importTime: null,
                dialer: null,
                routeId: pathTest.routeId,
                cycle: pathTest.cycle,
                stage: pathTest.stage,
                startTime: seq.startTime,
                endTime: seq.stopTime,
                testOrder: testPoint.order,
                status: DriveTestSeqStatus.SEQ_FINISHED,
                userId: this.currentUserId,
                groupingfield: pathTest.groupingfield
              })
              const bSaved = await noDialerDriveTestSeqDoc.save()
              console.log(`bSaved=${bSaved}, new drivetestseq seqid=${noDialerDriveTestSeqDoc.seqid}`)
            } else {
                var noDialerTestSeqDoc = this.testSeqService.rxCollection.newDocument({
                  seqid: seq.id,
                  pointId: testPoint.pointId,
                  testPointId: testPoint.id,
                  pathId: pathTest.pathId,
                  importTime: null,
                  dialer: null,
                  buildingId: pathTest.buildingId,
                  cycle: pathTest.cycle,
                  stage: pathTest.stage,
                  startTime: seq.startTime,
                  endTime: seq.stopTime,
                  testOrder: testPoint.order,
                  status: TestSeqStatus.SEQ_FINISHED,
                  userId: this.currentUserId,
                  groupingfield: pathTest.groupingfield
                })
                const bSaved = await noDialerTestSeqDoc.save()
                console.log(`bSaved=${bSaved}, new testseq seqid=${noDialerTestSeqDoc.seqid}`)
              }
          } catch (error: any) {
            const errMsg = `Failed saving ${pathTest.type} document (no rootTest avail from dialer)`;
            console.error(errMsg);
            console.error(error);
            this.testSeqService.publishError(error, this);
            this.componentErrorMsg$.next(errMsg);
          }
        } else {
          console.error(`Cannot save ${pathTest.type} test sequence to TestSeq collection (missing pathTest, testPoint or seq)`);
          console.error(`pathTest=${pathTest?._id},testPoint.id=${testPoint?.id},seq.id=${seq?.id}`);
        }
      } else {
        if ( testSeqDoc ) {
          try {
            await testSeqDoc.atomicPatch( {
              status: bIsDriveTest ? DriveTestSeqStatus.SEQ_FINISHED : TestSeqStatus.SEQ_FINISHED
            });
          } catch ( e: any ) {
            console.error(e)
            this.componentErrorMsg$.next("Could not update test seq document status to finished")
          }
        }
      }
      this.bRootTestActive = false;
    } catch (err) {
      this.pathTestService.publishError(err, this)
      const errMsg = this.pathTestService.extractErrorMsg(err)
      this.componentErrorMsg$.next(errMsg)
    }
    this.isSaving = false
    this.endProcessing()
  }

  getStatusName() {
    return activity_status.Names[this.testPoint.statusId] || ``
  }

  bStarting = false

  async startTest(testPoint: PathTestPoint|DriveTestPoint, seq: PathTestPointSeq | DriveTestPointSeq ) {

    console.log(`startTest(testPoint.id=${testPoint?.id},seq.id=${seq.id})`)
    seq.startTime = this.pathTestService.nowTimeStamp()
    seq.userId = this.currentUserId
    seq.stopTime = null

    const lastStatusId = testPoint.statusId

    this.bStarting = true
    try {
      let dialerConfig : DialerConfig = null

      console.log(`useDialer: ${this.useDialer}`)
      if ( this.useDialer ) {
        if ( !this.dialerClientService.dialerService?.bInitialized ) {
          throw new Error('Dialer not initialized (Make sure enabled on Home tab)')
        }
        const timeInfo = await firstValueFrom(this.dialerClientService.createTimeStatusObservable(5000))
        if ( timeInfo.warningMsg ) {
          throw new Error(timeInfo.warningMsg)
        }
        dialerConfig = this.buildDialerConfigFn()
        if ( dialerConfig && !dialerConfig.entries.some( entry => entry.enabled ) ) {
          throw new Error('Dialer is included but no handsets are enabled (check Dialer Config tab)')
        }
      }

      const [updatedTestPt, updatedSeq] = await this.saveTestPointSeqImplFn(testPoint?.id,seq,activity_status.Id.IN_PROGRESS)

      var start = new Date(seq.startTime)
      if ( this.timer ) {
        clearInterval(this.timer)
      }
      this.timer = setInterval(() => {
        var end = new Date()
        this.duration = end.getTime() - start.getTime()
      }, 1000);

      let statusMsg = this.pointTestLogService.textForStatusId(updatedTestPt.statusId)

      if ( dialerConfig ) {
        await this.runSequencer(start,dialerConfig)
      } else {
        statusMsg += ` *** dialer not used ***`
      }

      try {

        await this.pointTestLogService.logComment(seq.id,statusMsg)
      } catch ( error ) {
        this.appState.notifyError('Failed saving start comment. ' +
            this.pathTestService.extractErrorMsg(error),this,3)
      }
    } catch ( error ) {
      const errMsg = 'Error starting. ' + this.pathTestService.extractErrorMsg(error)
      console.error(errMsg)
      console.error(error)
      this.pathTestService.publishError(error,this)
      this.componentErrorMsg$.next(errMsg)
      if ( !this.seq.startTime ) {
        testPoint.statusId = lastStatusId
      }
    } finally {
      this.bStarting = false
    }
  }


  showDialerSeqId(seq: PathTestPointSeq | DriveTestPointSeq, testSeqDoc: TestSeqDoc | DriveTestSeqDoc){
    const initialState = {
      seqContext: {seq, type: this.pathTestDoc.type},
      activeTab: 'calls'
    }
    DialerClientTestSeqModalComponent.showModal(this.modalService, initialState)
  }


  async runSequencer(startTime:Date,dialerConfig: DialerConfig) {
    console.log('runSequencer()')
    try {
      //TODO - Give DriveTest and PathTest explicit test spec context types
      let mutualArgs = {
        testPointId: this.testPoint.id,
        pathTestId: this.pathTestDoc._id,
        cycle: this.pathTestDoc.cycle,
        stage: this.pathTestDoc.stage,
        startTime: startTime.toISOString(),
        testOrder: this.testPoint.order,
        userId: this.currentUserId,
        env: AppConfig.environment,
        groupingfield:this.pathTestDoc.groupingfield
        //status: TestSeqStatus.SEQ_FINISHED,

      };
      let testSpecArgs: DialerTestSpecContext;
      if(this.pathTestDoc.type == "drive test") {
        testSpecArgs = {
          ...mutualArgs,
          routeId: this.testPoint.routeId,
          name: this.testPoint.routeId,
          buildingId: null,
          pathId: null
        }

      } else if(this.pathTestDoc.type == "path test"){
        testSpecArgs = {
          ...mutualArgs,
          pointId: this.testPoint.pointId,
          name: this.testPoint.pointId,
          buildingId: this.pathTestDoc.buildingId,
          pathId: this.pathTestDoc.pathId,
        }
      }
      this.rootTest = null
      this.beginTestSubscription()
      this.rootTest = await this.dialerClientService.runSeq(testSpecArgs, dialerConfig)
      this.seq.dialerSeqId = this.rootTest.seqId
      await this.saveTestPointSeqImplFn(this.testPoint?.id,this.seq,activity_status.Id.IN_PROGRESS)
    } catch ( error ) {
      console.error(error)
      throw error
    }
  }

  beginTestSubscription() {
    console.log(`beginTestSubscription()`)
    this.dialerTestSubscription?.unsubscribe()
    const testSeqDoc = this.testSeqDoc;
    this.dialerTestSubscription = this.dialerTestService.rootTest$.subscribe( {
      next: async result => {
        if ( !result.success ) {
          console.warn(result.errors);
          return;
        }
        const rootTest = result?.test
        if ( !rootTest ) {
          return;
        }
        //console.dir(rootTest)
        if ( !testSeqDoc?.seqid || rootTest?.seqId !== testSeqDoc.seqid) {
          console.log(`rootTest from dialer ignored. seqid did not match (rootTest.seqId:${rootTest?.seqId}, active seqid:${testSeqDoc?.seqid})`);
          return;
        }
        this.bRootTestActive = !isFinishedTest(rootTest);
        console.log(`rootTest update from dialer. bRootTestActive=${this.bRootTestActive}`);
        this.rootTest = rootTest;
      }
    })

    this.networkStatusSub?.unsubscribe();
    this.networkStatusSub = this.dialerClientService.dialerService.networkStatus$.pipe(
        skip(1), //TODO - this assumes first status is 'ok'
        distinctUntilKeyChanged('eventType')
      ).subscribe( {
        next: async (status: NetworkStatus ) => {
          if ( this.seq?.id && this.seq.startTime && !this.seq.stopTime ) {
              await this.pointTestLogService.logComment(this.seq.id,`[Network] ${status.ok?'OK':'Error:'} ${status.error?.message||''}`)
          }
        }
      })
  }

  async saveTestSeqRootTest() {
    if ( !this.isLocalTestInProgress() ) {
      console.log(`saveTestSeqRootTest() - Ignoring save request since local test is not in progress.`);
      console.dir(this.testSeqDoc?.toJSON());
      console.dir(this.rootTest);
      return;
    }
    const lastStatus = this.rootTest?.status;
    if ( lastStatus != 'PAUSING' && this.rootTest.status != 'PAUSED' ) {
      await this.testSeqDoc.atomicPatch( { dialer: this.rootTest })
      await this.pointTestLogService.logComment(this.seq.id,`[Saved] Partial dialer test results saved with status ${this.rootTest?.status}.`)
    }
  }

  async enableDialer(bEnable=true){
    this.dialerClientService.enableDialer(bEnable)
    this.useDialer = bEnable
    this.useDialer$.next(this.useDialer)
  }

  async useBaroChanged() {
    this.appState.settings.setPathTestPointEditorUseBaro(this.useBaro)
  }

  async useDialerChanged() {
    this.appState.settings.setPathTestPointEditorUseDialer(this.useDialer)
  }

  async pause() {
    const testSeqDoc = this.testSeqDoc
    const seq = this.seq
    this.beginProcessing('Pausing')
    try {
      console.log(`pause(testSeqDoc=${testSeqDoc?.seqid},seq=${seq?.id})`)
      if ( !testSeqDoc || !seq ) {
        throw new Error(`Invalid state (testSeq=${testSeqDoc?.seqid}, seq=${seq?.id})`)
      }
      let rootTest = this.rootTest;
      let infoMsg = ''
      try {
        const pauseTestStatus = await firstValueFrom(race(
          this.dialerClientService.pauseTest(rootTest?.id),
          timer(30_000).pipe( switchMap(_=>throwError( () => new Error('Max wait for pause response exceeded.'))))
        ));
        rootTest.status = pauseTestStatus;
      } catch (error:any) {
        infoMsg = `Problem during pause attempt. ${this.testSeqService.extractErrorMsg(error)}`
        throw error;
      } finally {
        let status : string = rootTest.status
        if ( status == 'PAUSED') {
          status = 'Paused'
        } else if ( status == 'PAUSING') {
          status = 'Pausing'
        }
        await this.pointTestLogService.logComment(seq.id,`[${status}]${infoMsg}`)
      }
    } catch ( error ) {
      console.error(error)
      const errMsg = `Failed pausing dialer test. ${this.testSeqService.extractErrorMsg(error)}`
      this.componentErrorMsg$.next(errMsg)
      this.pathTestService.publishErrorObj(error,errMsg,this)
    } finally {
      this.endProcessing()
    }
  }

  async resume() {
    const testSeqDoc = this.testSeqDoc
    const seq = this.seq
    const rootTest = this.rootTest
    this.beginProcessing('Resuming')
    try {
      console.log(`resume()`)
      if ( !testSeqDoc || !seq ) {
        throw new Error(`Invalid state (testSeq=${testSeqDoc?.seqid}, seq=${seq?.id})`)
      }
      const timeInfo = await firstValueFrom(this.dialerClientService.createTimeStatusObservable(30_000))
      if ( !timeInfo || timeInfo.warningMsg ) {
        this.componentErrorMsg$.next('Cannot resume before dialer time is validated. '+ (timeInfo?.warningMsg||'No time info'));
        return;
      }
      const latestRootTest = (await firstValueFrom(this.dialerTestService.createFetchRootTest$(false))).test
      if ( !latestRootTest || rootTest.id !== latestRootTest.id ) {
        await this.pointTestLogService.logComment(seq.id,`[Missing] Resume dialer test requested but it is not active on the dialer box`)
        const revivedRootTest = await this.dialerClientService.reviveTest(rootTest);
        await this.pointTestLogService.logComment(seq.id,`[Recovered] Dialer test rebuilt from last known value with status ${revivedRootTest.status}`)
        try {
          const phones = this.dialerClientService.extractPhones(rootTest);
          this.appState.notifyInfo(`Reconnecting ${phones.length} phones.`,this,2);
          await this.phoneService.reconnectPhones( phones.map(phone=>phone.mac) );
        } catch ( error: any ) {
          this.appState.notifyError('Error reconnecting phones. ' + error?.message,this,5);
        }
      }
      this.beginTestSubscription(); // restart listening to get latest
      const testStatus = await this.dialerClientService.resumeTest(rootTest?.id)
      await testSeqDoc.atomicPatch( { status: testStatus }); //TBD - is this needed? it might start another sync for large drive test
      await this.pointTestLogService.logComment(seq.id,`[Resumed] Status is ${testStatus}.`)
    } catch ( error ) {
      console.error(error)
      const errMsg = `Failed resuming paused dialer test. ${this.testSeqService.extractErrorMsg(error)}`
      this.componentErrorMsg$.next(errMsg)
      this.pathTestService.publishErrorObj(error,errMsg,this)
    } finally {
      this.endProcessing()
    }
  }
}
